import { useRef } from 'react';
import { Portal } from '@mui/material';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import type { ApprovalDecisionAction } from '@/hooks/approval/useApprovalDecision';
import AiAssistantPanel from './index';
import { PanelFrame } from './styles';
import useDraggablePanel from './useDraggablePanel';
import useKeyboardViewportHeight from './useKeyboardViewportHeight';

/**
 * 결재 모달(1300) 위, MUI Snackbar(1400) 아래 — 패널 안에서 뜨는 토스트(복사·반려 결과)가 가려지지 않게.
 * 파일뷰어 AI 시트와 같은 값.
 */
const PANEL_Z_INDEX = 1350;

type Props = {
  open: boolean;
  isMobile: boolean;
  documentId: string;
  canDecide: boolean;
  onClose: () => void;
  onDecided?: (action: ApprovalDecisionAction) => void;
  canShare?: boolean;
  onShared?: () => void | Promise<void>;
};

/**
 * AI 어시스턴트 패널의 자리. 결재 상세는 페이지·PC 모달 2종·모바일 다이얼로그가 같은 ApprovalView 를
 * 쓰므로, 호스트마다 레이아웃을 바꾸는 대신 PC 는 우측 하단에 끌어 옮길 수 있는 플로팅 카드로 띄우고
 * 모바일은 바텀시트로 띄운다. 패널은 열려 있는 동안만 마운트한다 — 열 때마다 서버 이력을 다시 복원한다.
 */
const AiAssistantHost = ({
  open,
  isMobile,
  documentId,
  canDecide,
  onClose,
  onDecided,
  canShare,
  onShared,
}: Props) => {
  // 키보드가 열리면 시트를 보이는 영역 높이로 줄인다 — iOS 가 화면을 밀어 올려도 헤더~입력창이 다 보이게
  const keyboardViewportHeight = useKeyboardViewportHeight(isMobile && open);

  if (isMobile) {
    return (
      <MobileBottomSheet
        open={open}
        onClose={onClose}
        variant="light"
        height={
          keyboardViewportHeight != null
            ? `${keyboardViewportHeight}px`
            : // 위쪽은 뒤 화면이 살짝 보일 만큼만 남기고 최대한 높게 (PORTAL-15727)
              'calc(100dvh - 32px)'
        }
        heightMode="sheet"
        showHandle={false}
        notifyAppBar
        zIndex={PANEL_Z_INDEX}
      >
        {open && (
          <AiAssistantPanel
            // 문서가 바뀌면 입력·카드 상태까지 새 문서 기준으로 (패널은 열린 채 유지될 수 있다)
            key={documentId}
            documentId={documentId}
            isMobile
            onClose={onClose}
            canDecide={canDecide}
            onDecided={onDecided}
            canShare={canShare}
            onShared={onShared}
          />
        )}
      </MobileBottomSheet>
    );
  }

  if (!open) return null;

  return (
    <Portal>
      <FloatingPanel
        documentId={documentId}
        canDecide={canDecide}
        onClose={onClose}
        onDecided={onDecided}
        canShare={canShare}
        onShared={onShared}
      />
    </Portal>
  );
};

type FloatingPanelProps = Omit<Props, 'open' | 'isMobile'>;

/** PC 플로팅 카드. 드래그 훅이 프레임 ref 를 필요로 해서 별도 컴포넌트로 뗀다. */
const FloatingPanel = ({
  documentId,
  canDecide,
  onClose,
  onDecided,
  canShare,
  onShared,
}: FloatingPanelProps) => {
  const frameRef = useRef<HTMLDivElement | null>(null);
  const { offset, isDragging } = useDraggablePanel(frameRef);

  return (
    <PanelFrame
      ref={frameRef}
      role="complementary"
      data-dragging={isDragging ? 'true' : 'false'}
      style={{
        zIndex: PANEL_Z_INDEX,
        transform: `translate(${offset.x}px, ${offset.y}px)`,
        transition: isDragging ? 'none' : 'transform 120ms ease',
      }}
    >
      <AiAssistantPanel
        key={documentId}
        documentId={documentId}
        isMobile={false}
        onClose={onClose}
        canDecide={canDecide}
        onDecided={onDecided}
        canShare={canShare}
        onShared={onShared}
      />
    </PanelFrame>
  );
};

export default AiAssistantHost;
